/* eslint-disable */
import React, { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import Select from 'components/Select/selectAbout'
import Button from 'components/Button'
import TrashBox from 'assets/icons/trash-box'
import BoldAddNewIcon from 'assets/icons/bold-add-new'
import {
  ActionsSection,
  ActionsCard,
  SectionTitle,
  ColumnHeaders,
  RowsWrapper,
  ActionRow,
  DividerLine,
  PlaceholderCell,
  IconPlaceholder,
  AddButtonRow,
  TopMarginDiv
} from './style'
import {
  deleteMonitorResponseAction,
  requestMonitorResponseAction,
  requestUpdateMonitorResponseAction
} from 'api/monitoring'
import { showErrorToast, showSuccessToast } from 'utils/toast'

const createEmptyActionRow = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  dbId: null,
  level1: null,
  level2: null,
  level3: null
})

const findOptionById = (options, id) => {
  if (!options || !id) return null
  const found = options.find((option) => option.value === id)
  return found || null
}

const ActionSelectorCard = ({
  siteResponseId,
  labelText,
  optionsOne,
  optionsTwo,
  optionsThree,
  type,
  monitorResponseActionsData,
  isEditable
}) => {
  const [rows, setRows] = useState([createEmptyActionRow()])
  const isActionUnderway = type === 'action-underway'
  const isActionNeeded = type === 'action-needed'
  const [initialDataLoaded, setInitialDataLoaded] = useState(false)
  const apiItems = monitorResponseActionsData?.items

  useEffect(() => {
    if (apiItems?.length && !initialDataLoaded) {
      const relevantItems = apiItems.filter((item) => {
        if (isActionUnderway) {
          return item.action_underway === true
        }
        if (isActionNeeded) {
          return item.action_needed === true
        }
        return false
      })
      if (relevantItems.length) {
        const newRows = relevantItems.map((item) => ({
          id: item.id,
          dbId: item.id,
          level1: findOptionById(optionsOne, item.level1_action_id),
          level2: findOptionById(optionsTwo, item.level2_action_id),
          level3: findOptionById(optionsThree, item.level3_action_id)
        }))
        setRows(newRows)
      } else {
        setRows([createEmptyActionRow()])
      }
      setInitialDataLoaded(true)
    }
  }, [
    monitorResponseActionsData,
    initialDataLoaded,
    isActionUnderway,
    isActionNeeded,
    optionsOne,
    optionsTwo,
    optionsThree
  ])

  const lastRow = rows[rows.length - 1]
  const hasLevel3OptionsInLastRow =
    lastRow?.level2?.value && optionsThree.some((opt) => String(opt.level2_id) === String(lastRow.level2.value))
  let isLastRowComplete = Boolean(lastRow?.level1 && lastRow?.level2)
  if (isLastRowComplete && hasLevel3OptionsInLastRow) {
    isLastRowComplete = Boolean(lastRow.level3)
  }
  const canAddRow = isLastRowComplete

  const createNewMonitoringResponseAction = async (level1Value, level2Value, level3Value) => {
    try {
      const payload = {
        site_response_id: siteResponseId,
        level1_action_id: level1Value.value,
        level2_action_id: level2Value.value,
        ...(level3Value && { level3_action_id: level3Value.value }),
        action_needed: isActionNeeded,
        action_underway: isActionUnderway
      }
      const newActionResponse = await requestMonitorResponseAction(payload)
      return newActionResponse.id
    } catch (error) {
      console.error(error)
    }
  }

  const UpdateMonitoringResponseAction = async (id, level1Value, level2Value, level3Value) => {
    try {
      const payload = {
        level1_action_id: level1Value.value,
        level2_action_id: level2Value.value,
        ...(level3Value && { level3_action_id: level3Value.value })
      }
      await requestUpdateMonitorResponseAction(id, payload)
    } catch (error) {
      console.error(error)
    }
  }

  const handleSelectChange = (rowIndex, level, option) => {
    let updatedRow = null
    setRows((prev) =>
      prev.map((row, index) => {
        if (index !== rowIndex) {
          return row
        }
        let newRowState = { ...row }
        if (level === 'level1') {
          newRowState = { ...row, level1: option, level2: null, level3: null }
        } else if (level === 'level2') {
          newRowState = { ...row, level2: option, level3: null }
        } else if (level === 'level3') {
          newRowState = { ...row, level3: option }
        }
        updatedRow = newRowState
        return newRowState
      })
    )

    const hasLevel3Options = optionsThree.some((opt) => String(opt.level2_id) === String(updatedRow?.level2?.value))
    const isLevel2Final = !hasLevel3Options && level === 'level2'
    const isLevel3Final = hasLevel3Options && level === 'level3'

    if (option && updatedRow && (isLevel2Final || isLevel3Final)) {
      const isExistingRecord = Boolean(updatedRow.dbId)
      const actionId = isExistingRecord ? updatedRow.dbId : null
      const l1 = updatedRow.level1
      const l2 = updatedRow.level2
      const l3 = updatedRow.level3

      if (isExistingRecord) {
        UpdateMonitoringResponseAction(actionId, l1, l2, l3)
      } else {
        const createAndSetId = async () => {
          const newDbId = await createNewMonitoringResponseAction(l1, l2, l3)
          if (newDbId) {
            setRows((prevRows) =>
              prevRows.map((row, index) => {
                if (index === rowIndex) {
                  return {
                    ...row,
                    dbId: newDbId
                  }
                }
                return row
              })
            )
          }
        }
        createAndSetId()
      }
    }
  }

  const handleAddRow = () => {
    if (!canAddRow) {
      return
    }
    setRows((prev) => [...prev, createEmptyActionRow()])
  }

  const deleteMonitoringResponseAction = async (id) => {
    try {
      await deleteMonitorResponseAction(id)
      showSuccessToast(<FormattedMessage id="deleteConservationActionSuccess" />, 'showBorder')
    } catch (error) {
      showErrorToast(<FormattedMessage id="deleteConservationActionError" />, 'showBorder')
      console.error(error)
    }
  }

  const handleDeleteRow = (rowIndex) => {
    const rowToDelete = rows[rowIndex]
    const dbIdToDelete = rowToDelete?.dbId || null
    if (dbIdToDelete) {
      deleteMonitoringResponseAction(dbIdToDelete)
    }
    setRows((prev) => {
      const updated = prev.filter((_, index) => index !== rowIndex)
      if (!updated.length) {
        return [createEmptyActionRow()]
      }
      return updated
    })
  }

  const hasLevel3Children = (level2Value) => {
    return optionsThree.some((opt) => String(opt.level2_id) === String(level2Value))
  }

  const getAvailableLevel1Options = (currentRowIndex) => {
    const usedCombinations = rows
      .filter((_, index) => index !== currentRowIndex)
      .map((row) => ({
        l1: row.level1?.value,
        l2: row.level2?.value,
        l3: row.level3?.value
      }))
      .filter((c) => c.l1 && c.l2)
    return optionsOne.filter((l1Option) => {
      const l1Value = l1Option.value
      const potentialL2Options = optionsTwo.filter((l2Opt) => String(l2Opt.level1_id) === String(l1Value))
      if (potentialL2Options.length === 0) {
        return false
      }

      const isL2Available = potentialL2Options.some((l2Option) => {
        const l2Value = l2Option.value
        const hasL3Children = hasLevel3Children(l2Value)

        if (hasL3Children) {
          const potentialL3Options = optionsThree.filter((l3Opt) => String(l3Opt.level2_id) === String(l2Value))
          const usedL3Count = usedCombinations.filter(
            (c) => String(c.l1) === String(l1Value) && String(c.l2) === String(l2Value) && c.l3
          ).length
          return usedL3Count < potentialL3Options.length
        } else {
          const isComboUsed = usedCombinations.some(
            (c) => String(c.l1) === String(l1Value) && String(c.l2) === String(l2Value)
          )
          return !isComboUsed
        }
      })
      return isL2Available
    })
  }

  return (
    <ActionsSection>
      <ActionsCard>
        <SectionTitle>
          <FormattedMessage id={labelText} />
        </SectionTitle>
        <ColumnHeaders>
          <span style={{ gridColumn: 'span 1' }}>
            <FormattedMessage id="actionsLevel1" />
          </span>
          <span style={{ gridColumn: 'span 1' }}>
            <FormattedMessage id="actionsLevel2" />
          </span>
          <span style={{ gridColumn: 'span 1' }}>
            <FormattedMessage id="actionsLevel3" />
          </span>
        </ColumnHeaders>
        <RowsWrapper>
          {rows.map((row, index) => {
            const showLevel2Select = Boolean(row.level1)
            const showDelete = Boolean(row.level1)
            const visibleOptionsOne = getAvailableLevel1Options(index)
            const isRowSaved = Boolean(row.dbId)
            const selectedLevel1Value = row.level1?.value
            const baseOptionsTwo = optionsTwo.filter(
              (option) => String(option.level1_id) === String(selectedLevel1Value)
            )
            const usedL3Combos = rows
              .filter((_, rIndex) => rIndex !== index)
              .filter((r) => String(r.level1?.value) === String(selectedLevel1Value) && r.level3?.value)
              .map((r) => r.level3?.value)
            const visibleOptionsTwo = baseOptionsTwo.filter((opt) => {
              const l2Value = opt.value
              const hasL3Children = optionsThree.some((l3) => String(l3.level2_id) === String(l2Value))
              if (hasL3Children) {
                const potentialL3Options = optionsThree.filter((l3Opt) => String(l3Opt.level2_id) === String(l2Value))
                const usedL3Count = usedL3Combos.filter((usedL3Value) =>
                  potentialL3Options.some((pL3) => String(pL3.value) === String(usedL3Value))
                ).length
                return usedL3Count < potentialL3Options.length
              } else {
                const isComboUsed = rows.some(
                  (r, rIndex) =>
                    rIndex !== index &&
                    String(r.level1?.value) === String(selectedLevel1Value) &&
                    String(r.level2?.value) === String(l2Value)
                )
                return !isComboUsed
              }
            })
            const selectedLevel2Value = row.level2?.value
            const baseOptionsThree = optionsThree.filter(
              (option) => String(option.level2_id) === String(selectedLevel2Value)
            )
            const visibleOptionsThree = baseOptionsThree.filter((opt) => {
              const isUsed = rows.some(
                (r, rIndex) =>
                  rIndex !== index &&
                  String(r.level1?.value) === String(selectedLevel1Value) &&
                  String(r.level2?.value) === String(selectedLevel2Value) &&
                  String(r.level3?.value) === String(opt.value)
              )
              return !isUsed
            })
            const showLevel3Select = Boolean(row.level2) && baseOptionsThree.length > 0
            const commonProps = {
              height: '44px',
              marginBottom: '0px',
              marginTop: '0px',
              menuListMaxHeight: '400px',
              placeholder: 'selectAction',
              menuPlacement: 'auto',
              menuZindex: 99999
            }
            return (
              <React.Fragment key={row.id}>
                <ActionRow>
                  <Select
                    {...commonProps}
                    disabled={isRowSaved || !isEditable}
                    options={visibleOptionsOne}
                    value={row.level1}
                    onChange={(value) => handleSelectChange(index, 'level1', value)}
                    blueBg={true}
                  />
                  {showLevel2Select ? (
                    <Select
                      {...commonProps}
                      disabled={isRowSaved || !isEditable}
                      options={visibleOptionsTwo}
                      value={row.level2}
                      onChange={(value) => handleSelectChange(index, 'level2', value)}
                      blueBg={true}
                    />
                  ) : (
                    <PlaceholderCell />
                  )}
                  {showLevel3Select ? (
                    <Select
                      {...commonProps}
                      disabled={isRowSaved || !isEditable}
                      options={visibleOptionsThree}
                      value={row.level3}
                      onChange={(value) => handleSelectChange(index, 'level3', value)}
                      blueBg={true}
                    />
                  ) : (
                    <PlaceholderCell />
                  )}
                  {showDelete ? (
                    <Button
                      type="disalbeBorder"
                      height="44px"
                      width="44px"
                      marginTop="0px"
                      onClick={() => handleDeleteRow(index)}
                      withIcon
                      noPadding={true}
                      isDisabled={!isEditable}
                    >
                      <TrashBox />
                    </Button>
                  ) : (
                    <IconPlaceholder />
                  )}
                </ActionRow>
                {index !== rows.length - 1 && (
                  <TopMarginDiv>
                    <DividerLine />
                  </TopMarginDiv>
                )}
              </React.Fragment>
            )
          })}
        </RowsWrapper>
        <AddButtonRow>
          <Button
            type={canAddRow && isEditable ? 'primary' : 'defaultDisabled'}
            marginTop="0px"
            onClick={handleAddRow}
            isDisabled={!canAddRow || !isEditable}
            withIcon
            width="48px"
            height="48px"
            noPadding={true}
          >
            <BoldAddNewIcon />
          </Button>
        </AddButtonRow>
      </ActionsCard>
    </ActionsSection>
  )
}
export default ActionSelectorCard
